import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface GlobalRuntimeSettings {
  llmGatewayAiBindingTimeoutMs: number;
  llmGatewayAiBindingRunTimeoutMs: number;
  llmGatewayAiBindingFailureCooldownMs: number;
  llmGatewayLogFetchTimeoutMs: number;
  conversationSummaryEnabled: boolean;
  chatStreamSummaryAfterMessages: number;
  chatStreamSummaryTokenGuard: number;
  codeAgentSummaryAfterMessages: number;
  codeAgentSummaryTokenGuard: number;
  codeAgentSummaryTurnGuard: number;
  codeAgentSummaryToolIterationGuard: number;
  /** K5: ms to wait for the first Code Agent model delta before abort. */
  codeAgentFirstChunkTimeoutMs: number;
  /** K5: ms of silence between Code Agent model deltas before stall. */
  codeAgentStreamStallTimeoutMs: number;
  /** C4: max tool-call iterations per chat turn when no package override applies. */
  chatStreamMaxToolIterations: number;
  /** C4: per-package override keyed by upper-case Package.code. */
  chatStreamMaxToolIterationsByPackage: Record<string, number>;
  /** C1: when the chat orchestrator model is consulted. */
  /** C2: chat runtime cache TTLs (seconds). */
  chatRuntimeSessionContextTtlSeconds: number;
  chatRuntimeQuotaSnapshotTtlSeconds: number;
  chatRuntimeAgentConfigTtlSeconds: number;
  /** User-facing quota status low threshold percent. */
  quotaLowThresholdPercent: number;
  billingCreditsPerUsd: number;
  billingCostVarianceThresholdPercent: number;
  billingCostVarianceMinAbsUsd: number;
  billingFallbackImageUsd: number;
  billingOverheadFactor: number;
  billingTargetMargin: number;
  billingPaymentFeeRate: number;
  billingTaxRate: number;
  billingFxBuffer: number;
  billingMinUtilization: number;
  billingReportMaxDays: number;
  billingUnitCostWindowDays: number;
  /** Code Agent rollout flags. */
  codeAgentNativeToolMessages: boolean;
  codeAgentPromptCache: boolean;
  codeAgentWebTools: boolean;
  codeAgentDeferredTools: boolean;
  codeAgentPluginSuggestions: boolean;
  codeAgentPlanVerifyGate: boolean;
  codeAgentSubAgent: boolean;
  codeAgentBatchTurnBilling: boolean;
  codeAgentClarificationGate: boolean;
  codeAgentApiGroundingGuard: boolean;
  codeAgentToolOutputSpill: boolean;
  codeAgentCompactClearToolOutput: boolean;
  codeAgentParallelReadonlyTools: boolean;
  codeAgentBackgroundCommands: boolean;
  codeAgentUntrustedContentGuard: boolean;
  codeAgentEditDiagnostics: boolean;
  codeAgentPlanMode: boolean;
  codeAgentExploreTool: boolean;
  codeAgentMediaRead: boolean;
  codeAgentBrowserTools: boolean;
  /** Admin Agent (bubble chat): on/off switch and its usage / approval / output limits. */
  adminAgentEnabled: boolean;
  adminAgentMaxRequestsPerMinute: number;
  adminAgentMaxDailyTokens: number;
  adminAgentMaxPendingActions: number;
  adminAgentActionTtlSeconds: number;
  adminAgentReadTokenTtlSeconds: number;
  adminAgentMaxResultChars: number;
  adminAgentMaxBodyBytes: number;
  adminAgentSearchMaxResults: number;
  adminAgentSocketTicketTtlSeconds: number;
  /** Extra deny rules, `"[ANY|WRITE] /path/*"`, on top of the built-in denylist. */
  adminAgentExtraDenyPatterns: string[];
  /** `host` / `host/path-prefix` rules a provider image URL may be fetched from. */
  generatedImageAllowedHosts: string[];
}

export interface GlobalSettingsResponse {
  config: GlobalRuntimeSettings;
}

export const globalSettingsKeys = {
  all: ['global-settings'] as const,
  detail: () => [...globalSettingsKeys.all, 'detail'] as const,
};

const fetchGlobalSettings = async (): Promise<GlobalSettingsResponse> => {
  const response = await axiosInstance.get<ApiResponse<GlobalSettingsResponse>>(
    '/v1/admin/global-settings'
  );

  if (!response.data.data) {
    throw new Error('Global settings response is empty');
  }

  return response.data.data;
};

const updateGlobalSettings = async (
  config: GlobalRuntimeSettings
): Promise<GlobalSettingsResponse> => {
  const response = await axiosInstance.put<ApiResponse<GlobalSettingsResponse>>(
    '/v1/admin/global-settings',
    config
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Global settings response is empty');
  }

  return response.data.data;
};

export const useGlobalSettings = () => {
  return useQuery({
    queryKey: globalSettingsKeys.detail(),
    queryFn: fetchGlobalSettings,
    staleTime: 60 * 1000,
  });
};

export const useUpdateGlobalSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGlobalSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(globalSettingsKeys.detail(), data);
      queryClient.invalidateQueries({ queryKey: globalSettingsKeys.detail() });
    },
  });
};
